import { LoadingOutlined } from "@ant-design/icons";
import { Button, Icons, Select, Tooltip, Typography } from "@ster/ster-toolkit";
import { Divider } from "antd";
import { SelectValue } from "antd/lib/select";
import { Suspense, memo, useCallback, useMemo, useState } from "react";
import { useRecoilValue } from "recoil";

import { forecastConversionGroupsState } from "../../atoms";
import { Medium, Order } from "../../types";
import sharedStyles from "../shared.module.less";
import styles from "./Forecast.module.less";
import ForecastRows from "./ForecastRows";

const Forecast = ({
  medium,
  order,
  onDeleteConversionGroup,
  onAddConversionGroup,
}: {
  medium: Medium;
  order: Order;
  onDeleteConversionGroup: (value: string) => void;
  onAddConversionGroup: (value: string) => void;
}): JSX.Element | null => {
  const conversionGroups = useRecoilValue(
    forecastConversionGroupsState(medium),
  );

  const [selectedConversionGroup, setSelectedConversionGroup] = useState<
    string | null
  >(null);

  const handleChangeConversionGroup = useCallback(
    (value: SelectValue | undefined) => {
      setSelectedConversionGroup(value as string);
    },
    [],
  );

  const handleAddConversionGroup = useCallback(() => {
    if (selectedConversionGroup) {
      onAddConversionGroup(selectedConversionGroup);
      setSelectedConversionGroup(null);
    }
  }, [onAddConversionGroup, selectedConversionGroup]);

  const subOrders = useMemo(
    () =>
      order.subOrders
        .filter((s) => s.calculated?.grp && s.calculated.grp > 0)
        .map(({ period, calculated, packageCode, ...rest }) => ({
          ...rest,
          from: period[0],
          to: period[1],
          budget: calculated?.budget,
          grp: calculated?.grp,
          spotsPerDayChannel: calculated?.spotsPerDayChannel,
          packageCode: packageCode ?? "",
        })),
    [order.subOrders],
  );

  return (
    <section className={styles.forecast}>
      <div className={styles.forecastTitleWrapper}>
        <Typography.Title level={2}>Prognose</Typography.Title>
      </div>
      <div className={styles.forecastHeader}>
        <div className={styles.forecastCol}>
          <Icons.AdultsIcon width={40} height={40} fill="#fff" />
          <span className={styles.forecastCol__stat}>
            Doel
            <wbr />
            groep
          </span>
        </div>
        <div className={styles.forecastCol}>
          <Icons.CampaignIcon width={40} height={40} fill="#fff" />
          <span className={styles.forecastCol__stat}>
            Bereik binnen doelgroep
          </span>
        </div>
        <div className={styles.forecastCol}>
          <Icons.GroupIcon width={65} height={40} fill="#fff" />
          <span className={styles.forecastCol__stat}>Bereik in aantal</span>
        </div>
        <div className={styles.forecastCol}>
          <Icons.EyeIcon width={40} height={40} fill="#fff" />
          <span className={styles.forecastCol__stat}>
            GCF
            <Tooltip
              title="De gemiddelde contactfrequentie van een campagne is het aantal keer dat een bereikte kijker/luisteraar de spot gemiddeld heeft gezien of gehoord. Doorgaans adviseren wij minimaal drie tot vier contacten per campagne."
              placement="bottom"
              classNames={{ root: sharedStyles.tooltipWide }}
            >
              <span>
                <Icons.QuestionIcon fill="white" width={16} height={16} />
              </span>
            </Tooltip>
          </span>
        </div>
        <div className={styles.forecastCol}>
          <Icons.StatsIcon width={40} height={40} fill="#fff" />

          <span className={styles.forecastCol__stat}>
            GRP
            <Tooltip
              title="Afkorting voor gross rating point. Oftewel: je bruto bereik. 1 GRP staat voor één reclamecontact met één procent van de doelgroep. Met andere woorden: 1 GRP is één procent kijk- of luisterdichtheid binnen de doelgroep."
              placement="bottom"
              classNames={{ root: sharedStyles.tooltipWide }}
            >
              <span>
                <Icons.QuestionIcon fill="white" width={16} height={16} />
              </span>
            </Tooltip>
          </span>
        </div>
      </div>
      <Divider className={styles.forecastDivider} />
      <Suspense
        fallback={
          <div className={styles.forecastRow}>
            <div className={`${styles.forecastCol} ${styles.loading}`}>
              <span className={styles.forecastCol__value}>
                <LoadingOutlined />
              </span>
            </div>
          </div>
        }
      >
        <ForecastRows
          medium={medium}
          subOrders={subOrders}
          conversionGroups={order.forecastConversionGroups}
          onDeleteConversionGroup={onDeleteConversionGroup}
        />
      </Suspense>
      <div className={styles.addConversionGroup}>
        <Select.Search
          placeholder="Kies een extra doelgroep"
          optionFilterProp="children"
          onSelect={handleChangeConversionGroup}
          value={selectedConversionGroup}
          popupMatchSelectWidth={false}
          options={conversionGroups
            ?.filter(
              (s) =>
                order.forecastConversionGroups.find((x) => s.code === x) ===
                undefined,
            )
            .map((c) => ({
              value: c.code,
              label: c.displayName,
            }))}
        />
        <Button
          mode="tertiary"
          disabled={!selectedConversionGroup}
          onClick={handleAddConversionGroup}
        >
          <Icons.PlusIcon fill="#008ccc" />
          Voeg doelgroep toe
        </Button>
      </div>
    </section>
  );
};

export default memo(Forecast);
